import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { getCategoryIcon, ICON_OPTIONS, ICON_MAP } from "@/lib/constants";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, SlidersHorizontal, Info, Phone, Save, Pencil, Check, X, Plus } from "lucide-react";

export default function Settings() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [editingCat, setEditingCat] = useState(null);
  const [editValue, setEditValue] = useState("");

  const [phone, setPhone] = useState("");
  const [phoneSaving, setPhoneSaving] = useState(false);

  const [addOpen, setAddOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newIcon, setNewIcon] = useState("wrench");
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    api.get("/settings/categories").then((res) => setCategories(res.data)).catch(() => {}).finally(() => setLoading(false));
    api.get("/public/company").then((res) => setPhone(res.data.phone || "")).catch(() => {});
  }, []);

  const toggle = async (name, enabled) => {
    setSavingKey(name);
    setCategories((cs) => cs.map((c) => (c.name === name ? { ...c, enabled } : c)));
    try {
      const { data } = await api.patch("/settings/categories", { category: name, enabled });
      setCategories(data);
      toast.success(enabled ? `« ${name} » activée` : `« ${name} » désactivée`);
    } catch (e) {
      setCategories((cs) => cs.map((c) => (c.name === name ? { ...c, enabled: !enabled } : c)));
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSavingKey(null);
    }
  };

  const startEdit = (name) => { setEditingCat(name); setEditValue(name); };
  const cancelEdit = () => { setEditingCat(null); setEditValue(""); };

  const saveRename = async (oldName) => {
    const nn = editValue.trim();
    if (!nn || nn === oldName) { cancelEdit(); return; }
    try {
      const { data } = await api.patch("/settings/categories/rename", { old_name: oldName, new_name: nn });
      setCategories(data);
      toast.success(`Catégorie renommée en « ${nn} »`);
      cancelEdit();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const openAdd = () => { setNewName(""); setNewIcon("wrench"); setAddOpen(true); };

  const addCategory = async () => {
    const name = newName.trim();
    if (!name) { toast.error("Le nom ne peut pas être vide"); return; }
    setAdding(true);
    try {
      const { data } = await api.post("/settings/categories/add", { name, icon: newIcon });
      setCategories(data);
      toast.success(`Catégorie « ${name} » créée`);
      setAddOpen(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setAdding(false);
    }
  };

  const savePhone = async () => {
    setPhoneSaving(true);
    try {
      const { data } = await api.patch("/settings/company", { phone });
      setPhone(data.phone || "");
      toast.success("Numéro de téléphone mis à jour");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setPhoneSaving(false);
    }
  };

  const enabledCount = categories.filter((c) => c.enabled).length;

  return (
    <div className="p-5 sm:p-8 max-w-4xl mx-auto" data-testid="settings-page">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
          <SlidersHorizontal className="h-6 w-6 text-blue-600" /> Paramètres
        </h1>
        <p className="text-slate-500 mt-1">Configurez les coordonnées et les catégories de travaux du site.</p>
      </div>

      {/* Téléphone de l'entreprise */}
      <div className="mt-6 bg-white rounded-2xl border border-slate-200 shadow-sm p-5" data-testid="company-phone-card">
        <h2 className="font-display font-semibold text-slate-900 flex items-center gap-2"><Phone className="h-4.5 w-4.5 text-blue-600" /> Numéro de téléphone (bouton « Appelez-nous »)</h2>
        <p className="text-sm text-slate-500 mt-1">Ce numéro reste masqué sur le site : le bouton « Appelez-nous » le compose au clic. Laissez vide pour masquer le bouton.</p>
        <div className="mt-3 flex flex-col sm:flex-row gap-2 sm:items-center">
          <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="02 96 00 00 00" className="sm:max-w-xs" data-testid="company-phone-input" />
          <Button onClick={savePhone} disabled={phoneSaving} className="bg-blue-600 hover:bg-blue-700" data-testid="company-phone-save">
            {phoneSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-2" /> Enregistrer</>}
          </Button>
        </div>
      </div>

      <div className="mt-6 flex items-start gap-3 rounded-xl bg-blue-50 border border-blue-200 p-4">
        <Info className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
        <p className="text-sm text-blue-900 leading-relaxed">
          Seules les catégories <strong>activées</strong> apparaissent dans le formulaire client. Vous pouvez
          <strong> ajouter</strong>, <strong>renommer</strong> ou activer/désactiver une catégorie — les demandes déjà associées sont mises à jour automatiquement lors d'un renommage.
        </p>
      </div>

      <div className="mt-6 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden" data-testid="category-settings-list">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 bg-slate-50 gap-3">
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Catégories de travaux</span>
          <div className="flex items-center gap-3 shrink-0">
            <span className="text-xs font-medium text-slate-500 hidden sm:inline" data-testid="enabled-count">{enabledCount} activée(s) sur {categories.length}</span>
            <Button size="sm" onClick={openAdd} className="bg-blue-600 hover:bg-blue-700 h-8" data-testid="add-category-button">
              <Plus className="h-4 w-4 mr-1.5" /> Ajouter une catégorie
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="grid place-items-center py-20"><Loader2 className="h-7 w-7 animate-spin text-blue-600" /></div>
        ) : (
          <ul>
            {categories.map((c) => {
              const Icon = getCategoryIcon(c.icon);
              const isEditing = editingCat === c.name;
              return (
                <li key={c.name} className="flex items-center justify-between px-5 py-4 border-b border-slate-100 last:border-0 hover:bg-slate-50/60 transition-colors gap-3">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className={`h-9 w-9 rounded-lg grid place-items-center shrink-0 ${c.enabled ? "bg-blue-50" : "bg-slate-100"}`}>
                      <Icon className={`h-4.5 w-4.5 ${c.enabled ? "text-blue-600" : "text-slate-400"}`} />
                    </div>
                    {isEditing ? (
                      <div className="flex items-center gap-1.5 min-w-0 flex-1">
                        <Input value={editValue} onChange={(e) => setEditValue(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") saveRename(c.name); if (e.key === "Escape") cancelEdit(); }}
                          className="h-8 max-w-[220px]" data-testid={`category-rename-input-${c.name}`} autoFocus />
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-emerald-600" onClick={() => saveRename(c.name)} data-testid={`category-rename-save-${c.name}`}><Check className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400" onClick={cancelEdit}><X className="h-4 w-4" /></Button>
                      </div>
                    ) : (
                      <div className="min-w-0">
                        <p className={`font-medium truncate ${c.enabled ? "text-slate-900" : "text-slate-500"}`}>{c.name}</p>
                        <p className="text-xs text-slate-400">{c.enabled ? "Visible sur le formulaire public" : "Masquée"}</p>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {!isEditing && (
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-blue-600" onClick={() => startEdit(c.name)} data-testid={`category-rename-${c.name}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                    )}
                    {savingKey === c.name && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
                    <Switch checked={c.enabled} onCheckedChange={(v) => toggle(c.name, v)} disabled={savingKey === c.name} data-testid={`category-toggle-${c.name}`} />
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Dialog: Ajouter une catégorie */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-md" data-testid="add-category-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Plus className="h-5 w-5 text-blue-600" /> Nouvelle catégorie</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-slate-700 font-medium">Nom de la catégorie</Label>
              <Input value={newName} onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") addCategory(); }}
                placeholder="Ex: Toiture, Chauffage, Isolation..." className="mt-1.5" data-testid="new-category-name-input" autoFocus />
            </div>
            <div>
              <Label className="text-slate-700 font-medium">Icône</Label>
              <div className="mt-2 grid grid-cols-6 sm:grid-cols-8 gap-2 max-h-48 overflow-y-auto pr-1" data-testid="icon-picker">
                {ICON_OPTIONS.map((opt) => {
                  const Ic = ICON_MAP[opt.key];
                  const active = newIcon === opt.key;
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      title={opt.label}
                      onClick={() => setNewIcon(opt.key)}
                      data-testid={`icon-option-${opt.key}`}
                      className={`aspect-square rounded-lg grid place-items-center border transition-colors ${active ? "bg-blue-600 border-blue-600 text-white" : "bg-white border-slate-200 text-slate-500 hover:border-blue-300 hover:text-blue-600"}`}
                    >
                      <Ic className="h-4.5 w-4.5" />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddOpen(false)} data-testid="add-category-cancel">Annuler</Button>
            <Button onClick={addCategory} disabled={adding} className="bg-blue-600 hover:bg-blue-700" data-testid="add-category-confirm">
              {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-1.5" /> Créer</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
